import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/I18nContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Check, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/naoml62k_image.png";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeader, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const monri = searchParams.get("monri");
    if (monri) {
      if (monri === "success") {
        setStatus("success");
        refreshUser();
      } else {
        setStatus("error");
      }
      return;
    }
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        getAuthHeader()
      );

      if (response.data.payment_status === "paid") {
        setStatus("success");
        await refreshUser();
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    } catch (error) {
      console.error("Payment status check error:", error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-neutral-800 bg-neutral-900 shadow-2xl">
        <CardContent className="p-8 text-center">
          <img src={LOGO_URL} alt="Logo" className="h-16 mx-auto mb-6" />
          
          {status === "checking" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Nunito']">{t("payment_checking")}</h2>
              <p className="text-neutral-400 mt-2">{t("please_wait_short")}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto mb-4 animate-zoom-in">
                <Check className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Nunito']">{t("payment_success")}</h2>
              <p className="text-neutral-400 mt-2">{t("payment_success_msg")}</p>
              <Button 
                className="mt-6 bg-red-600 hover:bg-red-700 text-white rounded-lg px-8"
                onClick={() => navigate("/dashboard")}
                data-testid="go-dashboard-btn"
              >
                {t("go_dashboard")}
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-amber-600/20 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-2xl font-bold text-white font-['Nunito']">{t("payment_error")}</h2>
              <p className="text-neutral-400 mt-2">{t("payment_error_msg")}</p>
              <Button 
                className="mt-6 bg-red-600 hover:bg-red-700 text-white rounded-lg px-8"
                onClick={() => navigate("/dashboard")}
                data-testid="go-dashboard-error-btn"
              >
                {t("go_dashboard")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
